import type { UploadPurpose } from "../types";

// Content fields whose words are shown to teachers. Settings, IDs and enum
// values (type, source, ...) stay untranslated.
const TEXT_KEYS = new Set([
  "alt_text",
  "body",
  "caption",
  "description",
  "expected_action",
  "explanation",
  "feedback",
  "feedback_correct",
  "feedback_incorrect",
  "help_text",
  "instructions",
  "label",
  "left_text",
  "prompt",
  "right_text",
  "text",
  "title",
]);

// File fields: a translation is a different file (e.g. the Urdu video).
const FILE_KEYS: Record<string, UploadPurpose | null> = {
  audio_url: "audio",
  cover_image_url: "image",
  embed_url: null,
  file_url: "resource",
  image_url: "image",
  left_image_url: "image",
  poster_url: "image",
  subtitle_url: "subtitle",
  thumbnail_url: "thumbnail",
  transcript_url: "document",
  url: null,
  video_url: "video",
};

const LIST_LABELS: Record<string, string> = {
  blanks: "Blank",
  children: "Sub-question",
  fields: "Field",
  files: "File",
  images: "Image",
  match_pairs: "Pair",
  options: "Option",
  order_items: "Item",
  questions: "Question",
  slides: "Slide",
};

export interface TranslatableString {
  /** The English string, which is also the dictionary key. */
  source: string;
  /** Where it appears, e.g. "Question 2 › Option 1". */
  label: string;
  /** Set for file links; null for a link that can only be pasted. */
  file?: UploadPurpose | null;
}

function humanize(key: string): string {
  const words = key.replace(/_url$/, "").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Lists every translatable string in an item's content once, in reading
// order. Teachers see a translation wherever the same English string
// appears, so repeated strings ("Yes", "No") are translated once.
export function translatableStrings(content: unknown): TranslatableString[] {
  const found = new Map<string, TranslatableString>();
  const visit = (value: unknown, path: string[], key: string, inImages: boolean) => {
    if (typeof value === "string") {
      const source = value.trim();
      if (!source || found.has(value)) return;
      const label = [...path, humanize(key)].join(" › ");
      if (TEXT_KEYS.has(key)) found.set(value, { source: value, label });
      else if (key in FILE_KEYS) {
        const file = key === "url" && inImages ? "image" : FILE_KEYS[key];
        found.set(value, { source: value, label, file });
      }
      return;
    }
    if (Array.isArray(value)) {
      const name = LIST_LABELS[key] ?? humanize(key);
      value.forEach((entry, index) =>
        visit(entry, [...path, `${name} ${index + 1}`], "", key === "images" || inImages)
      );
      return;
    }
    if (value && typeof value === "object") {
      for (const [childKey, child] of Object.entries(value)) {
        visit(child, path, childKey, inImages);
      }
    }
  };
  visit(content, [], "", false);
  return [...found.values()];
}
